import React from 'react'
import { FileTextIcon, LinkIcon, PaperclipIcon, ExternalLinkIcon } from 'lucide-react'
import type { ProposalRequestDisplayData } from '../lib/proposalRequestDisplay'
import { getServiceTypeLabel, resolveRequestServiceType } from '../lib/serviceTypeOptions'
import type { ServiceCategoryOption } from '../lib/workTypeMapping'

export interface ProposalRequestContactDisplay {
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  company_name?: string
}

interface ProposalRequestTabPanelProps {
  request: ProposalRequestDisplayData
  contact?: ProposalRequestContactDisplay | null
  serviceCategories?: ServiceCategoryOption[]
  viewRequestHref?: string
}

function formatPropertyType(value?: string): string {
  if (!value) return 'N/A'
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ')
}

function formatStatus(status?: string): string {
  if (!status) return 'Unknown'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export function ProposalRequestTabPanel({
  request,
  contact,
  serviceCategories = [],
  viewRequestHref,
}: ProposalRequestTabPanelProps) {
  const serviceTypeLabel = getServiceTypeLabel(
    resolveRequestServiceType(request, serviceCategories)
  )

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{request.title || 'Proposal Request'}</h3>
            {request.request_number && (
              <p className="text-sm text-gray-600 mt-1">Request #{request.request_number}</p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {viewRequestHref && (
              <a
                href={viewRequestHref}
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                <ExternalLinkIcon className="w-4 h-4" />
                Open request
              </a>
            )}
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full ${
                request.status === 'converted'
                  ? 'bg-green-100 text-green-800'
                  : request.status === 'submitted'
                    ? 'bg-blue-100 text-blue-800'
                    : request.status === 'reviewing'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
              }`}
            >
              {formatStatus(request.status)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Service Type</label>
            <p className="mt-1 text-sm text-gray-900">{serviceTypeLabel || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Property Type</label>
            <p className="mt-1 text-sm text-gray-900">{formatPropertyType(request.property_type)}</p>
          </div>
          {request.square_footage != null && (
            <div>
              <label className="text-sm font-medium text-gray-500">Square Footage</label>
              <p className="mt-1 text-sm text-gray-900">
                {Number(request.square_footage).toLocaleString()} sq ft
              </p>
            </div>
          )}
          {request.estimated_budget != null && (
            <div>
              <label className="text-sm font-medium text-gray-500">Estimated Budget</label>
              <p className="mt-1 text-sm text-gray-900">
                ${Number(request.estimated_budget).toLocaleString()}
              </p>
            </div>
          )}
        </div>
        {request.description && (
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-500">Description</label>
            <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{request.description}</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-gray-500">Address</label>
            <p className="mt-1 text-sm text-gray-900">{request.property_address || 'N/A'}</p>
          </div>
          {request.property_city && (
            <div>
              <label className="text-sm font-medium text-gray-500">City</label>
              <p className="mt-1 text-sm text-gray-900">{request.property_city}</p>
            </div>
          )}
          {request.property_state && (
            <div>
              <label className="text-sm font-medium text-gray-500">State</label>
              <p className="mt-1 text-sm text-gray-900">{request.property_state}</p>
            </div>
          )}
          {request.property_zip && (
            <div>
              <label className="text-sm font-medium text-gray-500">ZIP Code</label>
              <p className="mt-1 text-sm text-gray-900">{request.property_zip}</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {request.preferred_start_date && (
            <div>
              <label className="text-sm font-medium text-gray-500">Preferred Start Date</label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(request.preferred_start_date).toLocaleDateString()}
              </p>
            </div>
          )}
          {request.preferred_completion_date && (
            <div>
              <label className="text-sm font-medium text-gray-500">Preferred Completion Date</label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(request.preferred_completion_date).toLocaleDateString()}
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-500">Urgency Level</label>
            <p className="mt-1 text-sm text-gray-900">
              {formatPropertyType(request.urgency_level || 'normal')}
            </p>
          </div>
        </div>
      </div>

      {contact && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Contact Name</label>
              <p className="mt-1 text-sm text-gray-900">{contact.contact_name || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-sm text-gray-900">{contact.contact_email || 'N/A'}</p>
            </div>
            {contact.contact_phone && (
              <div>
                <label className="text-sm font-medium text-gray-500">Phone</label>
                <p className="mt-1 text-sm text-gray-900">
                  <a href={`tel:${contact.contact_phone}`} className="text-blue-600 hover:text-blue-800">
                    {contact.contact_phone}
                  </a>
                </p>
              </div>
            )}
            {contact.company_name && (
              <div>
                <label className="text-sm font-medium text-gray-500">Company</label>
                <p className="mt-1 text-sm text-gray-900">{contact.company_name}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {(request.special_requirements ||
        request.accessibility_needs ||
        request.existing_contractor) && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Special Requirements</h3>
          {request.special_requirements && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-500">Special Requirements</label>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                {request.special_requirements}
              </p>
            </div>
          )}
          {request.accessibility_needs && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-500">Access Info</label>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                {request.accessibility_needs}
              </p>
            </div>
          )}
          {request.existing_contractor && (
            <div>
              <label className="text-sm font-medium text-gray-500">Existing Contractor</label>
              <p className="mt-1 text-sm text-gray-900">Yes</p>
              {request.contractor_details && (
                <p className="mt-2 text-sm text-gray-900 whitespace-pre-wrap">
                  {request.contractor_details}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PaperclipIcon className="w-5 h-5 text-gray-500" />
            File Attachments ({request.attachments.length})
          </h3>
          {request.attachments.length > 0 ? (
            <div className="space-y-2">
              {request.attachments.map((attachment, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg gap-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileTextIcon className="w-4 h-4 text-gray-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 truncate">
                        {attachment.name || 'Untitled'}
                      </p>
                      {attachment.size != null && (
                        <p className="text-xs text-gray-500">
                          {(attachment.size / 1024).toFixed(1)} KB
                          {attachment.type && ` • ${attachment.type}`}
                        </p>
                      )}
                    </div>
                  </div>
                  {attachment.url && (
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2.5 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 shrink-0"
                    >
                      View
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-6">No file attachments</p>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-gray-500" />
            Blueprint & Document Links ({request.blueprint_links.length})
          </h3>
          {request.blueprint_links.length > 0 ? (
            <div className="space-y-2">
              {request.blueprint_links.map((link, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {link.url ? (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                        >
                          {link.title || 'Untitled Link'}
                        </a>
                      ) : (
                        <p className="text-sm font-medium text-gray-900">
                          {link.title || 'Untitled Link'}
                        </p>
                      )}
                      {link.description && (
                        <p className="text-xs text-gray-600 mt-0.5">{link.description}</p>
                      )}
                    </div>
                    {link.url && (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 shrink-0"
                      >
                        Open
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-6">No blueprint links</p>
          )}
        </div>
      </div>

      {(request.internal_notes || request.review_notes) && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Staff Notes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {request.internal_notes && (
              <div>
                <label className="text-sm font-medium text-gray-500">Internal Notes</label>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                  {request.internal_notes}
                </p>
              </div>
            )}
            {request.review_notes && (
              <div>
                <label className="text-sm font-medium text-gray-500">Review Notes</label>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                  {request.review_notes}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {request.intake_notes.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Notes</h3>
          <div className="space-y-3">
            {request.intake_notes.map((note, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">
                  {new Date(note.timestamp).toLocaleString()}
                </p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.note}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(request.submitted_at || request.converted_at) && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm text-gray-600 space-y-1">
          {request.submitted_at && (
            <p>Submitted: {new Date(request.submitted_at).toLocaleString()}</p>
          )}
          {request.converted_at && (
            <p>Converted to proposal: {new Date(request.converted_at).toLocaleString()}</p>
          )}
        </div>
      )}
    </div>
  )
}
